import PropTypes from "prop-types";
import React from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { DefaultGrossDepartmental, GrossBonus } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { detachValidate, isValidMapErrors } from "@/Common/hooks/Validations";

// Layouts
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import renderContent from "./RenderContent";

// MUI
import { Grid } from "@mui/material";

const combinedOemTabNames = {
	service: "Mechanical",
	bodyShop: "Body Shop",
	parts: "Parts",
};

const getMakeIdentifier = (make, dealerCodeFS, locationOEMs = []) => {
	if (!locationOEMs?.length || !dealerCodeFS) {
		return make;
	}
	const oem = locationOEMs.find((item) => item.oemName === make && item.dealerCodeFS === dealerCodeFS);
	return oem?.oemDisplayName || make;
};

const getMakes = (makeMetrics = [], excludesMakes = [], locationOEMs = []) => {
	const makes = makeMetrics
		.filter((element) => element.make && !excludesMakes.includes(element.make))
		.map((item) => getMakeIdentifier(item.make, item.dealerCodeFS, locationOEMs));
	return [...new Set(makes)];
};

const getMappedMakes = (makes = [], locationOEMs = []) =>
	makes.map((item) => ({ ...item, identifier: getMakeIdentifier(item.make, item.dealerCodeFS, locationOEMs) }));

const getContentTabs = (makes = []) => [...makes];

const getSanitizedMakes = (makes = []) => {
	return makes.map((definition) => {
		delete definition?.identifier;
		return definition;
	});
};

const mapModelToPropagate = (content, tabs = null) => {
	const sanitizedMakes = getSanitizedMakes(content?.makes || []);
	const sanitizedContent = { ...content, makes: sanitizedMakes };
	return {
		...sanitizedContent,
		combineOEMs: Boolean(tabs?.combineOEMs),
		isGuaranteedDraw: Boolean(tabs?.isGuaranteedDraw),
	};
};

const validateBeforeSend = (tiers = []) => {
	const validation = detachValidate([
		{
			definition: {
				useKey: "totalGross",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01, Dollar: 0.01 }, // Add delta on upper value
							distinct: 0,
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers || [],
		},
		{
			definition: {
				useKey: "grossGrowth",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01, Dollar: 0.01 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid Gross Growth tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers?.[0]?.bonuses || [],
		},
		{
			definition: {
				useKey: "bonuses",
				operators: {
					conditions: [
						{
							order: "ROWS",
							type: "STEP",
							on: "value",
							compare: "<=", // If current <= next is good,
						},
						{
							order: "COLUMNS",
							type: "STEP",
							on: "value",
							compare: "<",
							deltas: { Dollar: 0, Percent: 0 },
						},
					],
				},
				errorsMap: ["bonus"],
				errorsMapOn: "bonuses",
				message: "Incorrect bonuses definition",
				validationType: "MATRIX",
			},
			section: tiers,
		},
	]);
	return isValidMapErrors(validation);
};

const isAllValid = (errors = []) => !errors.length || [...errors].every((error) => error);

const resolveIsEmptyBonus = (tiers = []) => tiers.length === 1 && tiers[0].bonuses?.length === 1 && tiers[0].bonuses[0].bonus?.value === 0;

const isEmptyBonusError = (makes = []) => {
	return makes.reduce((acc, make) => {
		const isEmptyBonus = resolveIsEmptyBonus(make.tiers || []);
		return acc && isEmptyBonus;
	}, true);
};

export default function GrossDepartmental(props) {
	const {
		sectionId = "grossDepartmental",
		content = {
			makes: [],
			combineOEMs: false,
			isGuaranteedDraw: false,
		},
		locationOEMs = [],
		excludesOEMs = [],
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		useSectionsErrors = () => {},
	} = props;

	// Hooks
	const { errors, getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// OEMs/Makes treatments
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const makes = React.useMemo(() => getMakes(content?.makes || [], excludesOEMs, locationOEMs), [content, excludesOEMs]);

	// States
	const [model, setModel] = React.useState(null);
	const [tabIndex, setTabIndex] = React.useState(0);

	// Special empty state for errors
	const hasEmptyBonuses = isEmptyBonusError(model?.content?.makes || []);

	// Error handler
	const tabsErrors = React.useMemo(() => {
		if (errors.size && makes?.length) {
			const localErrors = Array.from(Array(makes.length)).map(() => false);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors
			makes.forEach((make, index) => {
				const errorKey = getErrorKey(sectionId, make);
				localErrors[index] = Boolean(listErrors.includes(errorKey) || hasEmptyBonuses);
			});

			return localErrors;
		}

		return [];
	}, [sectionId, makes, errors, hasEmptyBonuses, getErrorKey]);

	// Actions handlers
	const updateAll = (section, data) => {
		setModel({ ...data });
		const storedErrors = Object.values(data?.content?.makes)?.map((make) => validateBeforeSend(make?.tiers || []));
		const dataToPropagate = mapModelToPropagate(data.content, data.tabsProps);
		const isValidModel = isAllValid(storedErrors);
		onActionUpdate?.(section, null, { ...dataToPropagate }, isValidModel);
	};

	const handleUpdateSwitches = (section, area, value, selectedIndex) => {
		const updatedModel = { ...model };
		const identifier = combinedOemTabNames?.[section] || "New Vehicles";

		if (area === "combineOEMs") {
			if (value) {
				updatedModel["content"]["makes"] = [GrossBonus({ identifier, make: identifier, dealerCodeFS: "" }, section)];
			} else {
				updatedModel["content"] = DefaultGrossDepartmental(locationOEMs, section);
			}
		}

		// Updating current switch
		if (area === "applyTiersRetroactively") {
			updatedModel["content"]["makes"][selectedIndex][area] = value;
		} else {
			updatedModel["tabsProps"][area] = value;
		}

		updateAll(section, updatedModel);
	};

	const handleUpdate = (section, area, data) => {
		const updatedModel = { ...model };
		removeSectionError(section, area); // Remove section error

		updatedModel?.content.makes?.forEach((element, key) => {
			if (element.identifier === area) {
				updatedModel["content"]["makes"][key] = { ...element, ...data };
			}
		});

		updateAll(section, updatedModel);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	React.useEffect(() => {
		hasEmptyBonuses ? addSectionError(sectionId, "emptyModels") : removeSectionError(sectionId, "emptyModels");
	}, [sectionId, hasEmptyBonuses, addSectionError, removeSectionError]);

	React.useEffect(() => {
		const clonedContent = clone(content);
		const mappedMakes = getMappedMakes(clonedContent?.makes || [], locationOEMs);

		setModel({
			tabsProps: {
				tabs: getContentTabs([...makes]),
				combineOEMs: Boolean(clonedContent.combineOEMs),
				isGuaranteedDraw: Boolean(clonedContent.isGuaranteedDraw),
				errors: [],
			},
			content: { ...clonedContent, makes: [...mappedMakes] },
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content, makes]);

	React.useEffect(() => {
		return () => {
			setModel(null);
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.content?.makes?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={tabsErrors}
							content={renderContent({
								makes: model?.content?.makes || [],
								sectionId,
								readOnly,
								handleUpdate,
								handleValidation,
							})}
							topActions={[
								<CustomizedSwitches
									key={"isGuaranteedDraw"}
									textLeft={true}
									textChecked={"Guaranteed Draw"}
									textUnChecked={"Guaranteed Draw"}
									checked={Boolean(model?.tabsProps.isGuaranteedDraw)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "isGuaranteedDraw", value)}
									tooltip={{
										title: "Guarantee the draw.",
										placement: "bottom",
										key: "default-commissions-guarantee-draw-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"combineOEMs"}
									textLeft={true}
									textChecked={"Combine OEMs"}
									textUnChecked={"Combine OEMs"}
									checked={Boolean(model?.tabsProps?.combineOEMs)}
									disabled={readOnly || Boolean(locationOEMs?.length <= 1)}
									onChange={(value) => handleUpdateSwitches(sectionId, "combineOEMs", value)}
									tooltip={{
										title: "Combines OEMs into a single definition.",
										placement: "bottom",
										key: "market-share-simultaneous-ranking-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={Boolean(model?.content?.makes?.[tabIndex]?.applyTiersRetroactively)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value, tabIndex)}
								/>,
							]}
							onTabChange={(index) => {
								setTabIndex(index);
							}}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
GrossDepartmental.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		combineOEMs: PropTypes.bool,
		isGuaranteedDraw: PropTypes.bool,
	}),
	locationOEMs: PropTypes.array,
	excludesOEMs: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};
